import { Box, Icon, Link } from '@amzn/awsui-components-core';
import React, { useState, useEffect } from 'react';
import { NavItem } from './useNavigationItems';
import { WelcomeContentDetailsEntity } from '../das-finsuite/admin-components/AdminModels';
import { sanitizeHtml } from 'src/utilities/SanitizeHtml';
import { ContactList } from '../das-finsuite/widgets/welcome-widget/ContactList';

/** Converts display text to a valid HTML ID slug (no spaces or special characters). */
const toSlug = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

/**
 * ExpandableGroup renders a collapsible section with child nav items.
 */
export const ExpandableGroup: React.FC<{
  item: NavItem;
  activeHref: string;
  onNavigate: (href: string, text: string, external?: boolean) => void;
}> = ({ item, activeHref, onNavigate }) => {
  const hasActiveChild = item.children?.some((child) => activeHref === child.href) ?? false;
  // Use || (not ??) so an explicit defaultExpanded={false} never hides an active child
  const [expanded, setExpanded] = useState(item.defaultExpanded || hasActiveChild);

  // Re-expand whenever the active route changes to a child
  useEffect(() => {
    if (hasActiveChild) setExpanded(true);
  }, [hasActiveChild]);

  const isGroupActive = activeHref === item.href;
  const headerClass = `side-nav-v2-group-header${isGroupActive ? ' side-nav-v2-group-header--active' : ''}`;

  return (
    <div className="side-nav-v2-group">
      <div className={headerClass}>
        <Link
          href={item.href}
          onFollow={(e) => {
            e.preventDefault();
            onNavigate(item.href, item.text);
          }}
        >
          {item.text}
        </Link>
        <button className="side-nav-v2-group-toggle" onClick={() => setExpanded(!expanded)}>
          {expanded ? <Icon name="angle-up" /> : <Icon name="angle-down" />}
        </button>
      </div>
      {expanded && (
        <div className="side-nav-v2-group-children">
          {item.children?.map((child, i) => (
            <NavLink key={`${child.href}-${i}`} item={child} activeHref={activeHref} onNavigate={onNavigate} />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * NavLink renders a single navigation link.
 */
export const NavLink: React.FC<{
  item: NavItem;
  activeHref: string;
  onNavigate: (href: string, text: string, external?: boolean) => void;
}> = ({ item, activeHref, onNavigate }) => {
  const isActive = activeHref === item.href;
  const className = `side-nav-v2-link${isActive ? ' side-nav-v2-link--active' : ''}${item.bold ? ' side-nav-v2-link--bold' : ''}${
    item.indented ? ' side-nav-v2-link--indented' : ''
  }`;

  return (
    <div className={className}>
      <Link
        href={item.href}
        external={item.external}
        onFollow={(e) => {
          if (!item.external) {
            e.preventDefault();
            onNavigate(item.href, item.text);
          }
        }}
      >
        {item.text}
      </Link>
    </div>
  );
};

const AdminSidePanelWidget: React.FC<{
  welcomeItem: WelcomeContentDetailsEntity;
  index: number;
  isLast: boolean;
}> = ({ welcomeItem, index, isLast }) => {
  return (
    <div className="welcome-content-item" id={`welcome-item-${welcomeItem.id || toSlug(welcomeItem.displayText || '')}`}>
      {/* Row 1: Header and Contact List */}
      <div className="welcome-content-header">
        <div className="welcome-content-title">
          {welcomeItem.link ? (
            <Link variant="secondary" external href={welcomeItem.link}>
              {welcomeItem.displayText}
            </Link>
          ) : (
            <Box variant="awsui-key-label" color="text-status-info" fontSize="body-m" fontWeight="normal">
              {welcomeItem.displayText}
            </Box>
          )}
        </div>
        <div className="welcome-content-contact">{welcomeItem.pointOfContact && <ContactList userAlias={welcomeItem.pointOfContact} />}</div>
      </div>

      {/* Row 2: Description */}
      <Box fontSize="body-s" fontWeight="normal" padding={{ top: 'xxxs', bottom: 'xs' }}>
        <div
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(welcomeItem.description || '')
          }}
        />
      </Box>

      {/* Horizontal bar */}
      {!isLast && <div className="horizontal-bar"></div>}
    </div>
  );
};
